<template>
  <section class="invoice-preview-wrapper">
    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col
            md="12"
            class="d-flex justify-content-between align-items-center mb-2"
          >
            <h2 class="mb-0">
              {{ $t("reception.cashier-invoices") }}
            </h2>
            <b-button-group size="sm">
              <b-button
                :variant="viewMode === 'cards' ? 'primary' : 'outline-primary'"
                @click="viewMode = 'cards'"
              >
                <feather-icon
                  icon="GridIcon"
                  size="16"
                  class="mr-50"
                />
                {{ $t("common.card_view") }}
              </b-button>
              <b-button
                :variant="viewMode === 'list' ? 'primary' : 'outline-primary'"
                @click="viewMode = 'list'"
              >
                <feather-icon
                  icon="ListIcon"
                  size="16"
                  class="mr-50"
                />
                {{ $t("common.list_view") }}
              </b-button>
            </b-button-group>
          </b-col>
        </b-row>

        <!-- Status Tabs -->
        <b-row>
          <b-col cols="12">
            <div class="status-tabs-wrapper">
              <b-button-group class="status-tabs-custom">
                <b-button
                  v-for="statusOption in statusOptions"
                  :key="statusOption.value"
                  :variant="
                    filters.status === statusOption.value
                      ? 'primary'
                      : 'outline-primary'
                  "
                  class="status-tab-btn"
                  @click="handleStatusTabChange(statusOption.value)"
                >
                  {{ statusOption.label }}
                </b-button>
              </b-button-group>
            </div>
          </b-col>
        </b-row>

        <!-- Filters Section -->
        <b-row>
          <b-col
            md="3"
            cols="12"
          >
            <b-form-group
              :label="$t('common.type')"
              label-for="invoice-type-filter"
            >
              <v-select
                id="invoice-type-filter"
                v-model="filters.invoiceType"
                :options="invoiceTypeOptions"
                :reduce="(option) => option.value"
                :placeholder="$t('common.type')"
                @input="fetchInvoices"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            cols="12"
          >
            <b-form-group
              :label="$t('common.patient')"
              label-for="patient-name-filter"
            >
              <b-form-input
                id="patient-name-filter"
                v-model="filters.patientName"
                :placeholder="$t('reception.patient-name')"
                @keydown.enter="fetchInvoices"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            cols="12"
          >
            <b-form-group
              :label="$t('common.invoice-number')"
              label-for="invoice-number-filter"
            >
              <b-form-input
                id="invoice-number-filter"
                v-model="filters.invoiceNumber"
                :placeholder="$t('common.invoice-number')"
                @keydown.enter="fetchInvoices"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            cols="12"
          >
            <b-form-group
              :label="$t('common.date')"
              label-for="date-filter"
            >
              <b-form-datepicker
                id="date-filter"
                v-model="filters.date"
                :max="new Date().toISOString().slice(0, 10)"
                :placeholder="$t('common.select-date')"
                today-button
                reset-button
                close-button
                @input="fetchInvoices"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="mt-2">
          <b-col
            cols="12"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-button
              variant="primary"
              @click="fetchInvoices"
            >
              {{ $t("common.search") }}
              <b-icon-search class="ml-1" />
            </b-button>
            <b-button
              variant="outline-secondary"
              class="ml-2"
              :title="$t('common.reset')"
              @click="resetFilters"
            >
              <feather-icon
                icon="RotateCwIcon"
                size="16"
              />
            </b-button>
          </b-col>
        </b-row>

        <!-- Bulk Selection Header -->
        <b-row
          v-if="selectedInvoices.length > 0"
          class="mt-3 mb-3"
        >
          <b-col cols="12">
            <b-card class="bulk-selection-header">
              <div
                class="d-flex justify-content-between align-items-center flex-wrap"
              >
                <div class="d-flex align-items-center flex-wrap">
                  <div class="mr-3 mb-2 mb-md-0">
                    <strong>
                      {{ $t("common.selected") }}: {{ selectedInvoices.length }}
                      {{ $t("common.invoices") }}
                    </strong>
                  </div>
                  <div class="mr-3 mb-2 mb-md-0">
                    <strong>
                      {{ $t("common.total") }}:
                      <span class="text-success">
                        {{ formatPrice(selectedInvoicesTotal) }}
                      </span>
                    </strong>
                  </div>
                </div>
                <div class="d-flex align-items-center flex-wrap gap-2">
                  <b-button
                    v-if="
                      selectedInvoices.length > 0 &&
                        selectedInvoices.every(
                          (id) =>
                            this.invoices.find((inv) => inv.id === id).status ===
                            'pending'
                        )
                    "
                    variant="primary"
                    size="sm"
                    :disabled="bulkUpdating"
                    @click="bulkUpdateStatus('approved')"
                  >
                    {{ $t("common.approve") }}
                  </b-button>
                  <b-button
                    variant="success"
                    size="sm"
                    :disabled="bulkUpdating || !canBulkPrint"
                    @click="bulkPrintInvoices"
                  >
                    <feather-icon
                      icon="PrinterIcon"
                      size="14"
                      class="mr-1"
                    />
                    {{ $t("common.print-selected") }}
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="clearSelection"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                      class="mr-1"
                    />
                    {{ $t("common.clear-selection") }}
                  </b-button>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col>
            <!-- List View -->
            <b-table
              v-if="viewMode === 'list' && !loading"
              :items="invoices"
              responsive
              :fields="tableColumns"
              primary-key="id"
              class="position-relative"
              show-empty
              :empty-text="$t('common.no-invoices-yet')"
            >
              <template #head(select)>
                <b-form-checkbox
                  :checked="areAllInvoicesSelected"
                  :indeterminate="areSomeInvoicesSelected"
                  @change="toggleSelectAll"
                />
              </template>
              <template #cell(select)="data">
                <b-form-checkbox
                  :checked="isInvoiceSelected(data.item.id)"
                  @change="toggleInvoiceSelection(data.item.id, $event)"
                />
              </template>
              <template #cell(surgery_or_service)="data">
                <span class="text-nowrap text-dark">
                  {{ getSurgeryOrServiceTitle(data.item) }}
                </span>
              </template>
              <template #cell(invoice_no)="data">
                <span class="text-nowrap font-weight-bold text-dark">
                  #{{ data.item.invoice_no }}
                </span>
              </template>
              <template #cell(patient_name)="data">
                <span class="text-nowrap text-dark">
                  {{
                    getNestedProperty(data.item, "patient_card.patient.name") ||
                      "N/A"
                  }}
                </span>
              </template>
              <template #cell(amount)="data">
                <span class="text-nowrap font-weight-bold text-success">
                  {{ formatPrice(data.item.amount) }}
                </span>
              </template>
              <template #cell(type)="data">
                <b-badge
                  variant="light"
                  class="text-dark"
                >
                  {{ getInvoiceTypeName(data.item.type) }}
                </b-badge>
              </template>
              <template #cell(status)="data">
                <CustomChangeInvoiceStatusDropDown
                  v-if="data.item.status !== 'printed'"
                  name="cashierInvoices"
                  endpoint="invoices"
                  :data="data.item"
                  @onEmit="handleStatusChange(data.item)"
                  @onStatusChange="handleStatusChangeRequest"
                />
                <b-badge
                  v-else
                  variant="info"
                  pill
                >
                  {{ data.item.status }}
                </b-badge>
              </template>
              <template #cell(date)="data">
                <span class="text-nowrap text-dark">{{ data.item.date }}</span>
              </template>
              <template #cell(actions)="data">
                <div class="text-nowrap d-flex">
                  <b-button
                    v-if="
                      canCreateRefundInvoices &&
                        data.item.type !== 'refund' &&
                        isInvoiceRefundable(data.item)
                    "
                    size="sm"
                    pill
                    variant="outline-danger"
                    class="mb-75 mx-1"
                    :disabled="creatingRefund"
                    @click="openCreateRefundModal(data.item)"
                  >
                    <feather-icon
                      icon="RotateCcwIcon"
                      size="14"
                      class="mr-1"
                    />
                    {{ $t("reception.create-refund-invoice") }}
                  </b-button>
                  <!-- Print button only for approved invoices -->
                  <b-button
                    v-if="data.item.status === 'approved'"
                    size="sm"
                    pill
                    variant="primary"
                    class="mb-75 mx-1"
                    @click="showInvoicePrint(data.item)"
                  >
                    <feather-icon
                      icon="PrinterIcon"
                      size="14"
                      class="mr-1"
                    />
                    {{ $t("reception.Print Invoice") }}
                  </b-button>
                </div>
              </template>
            </b-table>

            <!-- Card View -->
            <div v-else-if="viewMode === 'cards' && !loading">
              <b-row>
                <b-col
                  v-for="invoice in invoices"
                  :key="invoice.id"
                  cols="12"
                  md="6"
                  lg="3"
                  class="mb-3"
                >
                  <b-card
                    no-body
                    class="invoice-card h-100"
                  >
                    <!-- Card Header -->
                    <div class="card-header-custom">
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div class="d-flex align-items-center">
                          <b-form-checkbox
                            :checked="isInvoiceSelected(invoice.id)"
                            class="mr-2"
                            @change="toggleInvoiceSelection(invoice.id, $event)"
                          />
                          <h5 class="invoice-number mb-0">
                            #{{ invoice.invoice_no }}
                          </h5>
                        </div>
                        <CustomChangeInvoiceStatusDropDown
                          v-if="invoice.status !== 'printed'"
                          name="cashierInvoices"
                          endpoint="invoices"
                          :data="invoice"
                          @onEmit="handleStatusChange(invoice)"
                          @onStatusChange="handleStatusChangeRequest"
                        />
                        <b-badge
                          v-else
                          variant="info"
                          pill
                        >
                          {{ invoice.status }}
                        </b-badge>
                      </div>
                    </div>

                    <!-- Card Body -->
                    <b-card-body class="card-body-custom">
                      <!-- Invoice Amount -->
                      <div class="invoice-amount-section mb-3">
                        <div class="amount-label">
                          {{ $t("common.amount") }}
                        </div>
                        <div class="amount-value">
                          {{ formatPrice(invoice.amount) }}
                        </div>
                      </div>

                      <!-- Patient Info -->
                      <div class="info-section mb-2">
                        <div class="info-item">
                          <feather-icon
                            icon="UserIcon"
                            size="16"
                            class="info-icon mr-1"
                          />
                          <span class="info-label">{{
                            $t("common.patient")
                          }}</span>
                          <span class="info-value">
                            {{
                              getNestedProperty(
                                invoice,
                                "patient_card.patient.name"
                              ) || "N/A"
                            }}
                          </span>
                        </div>
                      </div>

                      <!-- Surgery/Service Title -->
                      <div class="info-section mb-2">
                        <div class="info-item">
                          <feather-icon
                            icon="FileTextIcon"
                            size="16"
                            class="info-icon mr-1"
                          />
                          <span class="info-label">{{
                            $t("surgery.surgery-or-service") ||
                              "Surgery/Service"
                          }}</span>
                          <span class="info-value">
                            {{ getSurgeryOrServiceTitle(invoice) }}
                          </span>
                        </div>
                      </div>

                      <!-- Invoice Type -->
                      <div class="info-section mb-2">
                        <div class="info-item">
                          <feather-icon
                            icon="TagIcon"
                            size="16"
                            class="info-icon mr-1"
                          />
                          <span class="info-label">{{
                            $t("common.type")
                          }}</span>
                          <b-badge
                            variant="light"
                            class="info-value"
                          >
                            {{ getInvoiceTypeName(invoice.type) }}
                          </b-badge>
                        </div>
                      </div>

                      <!-- Date -->
                      <div class="info-section mb-2">
                        <div class="info-item">
                          <feather-icon
                            icon="CalendarIcon"
                            size="16"
                            class="info-icon mr-1"
                          />
                          <span class="info-label">{{
                            $t("common.date")
                          }}</span>
                          <span class="info-value">{{ invoice.date }}</span>
                        </div>
                      </div>

                      <!-- Description -->
                      <div
                        v-if="invoice.description"
                        class="info-section mb-2"
                      >
                        <div class="description-text">
                          {{ invoice.description }}
                        </div>
                      </div>
                    </b-card-body>

                    <!-- Card Footer with Actions -->
                    <div class="card-footer-custom">
                      <b-button
                        v-if="
                          canCreateRefundInvoices &&
                            invoice.type !== 'refund' &&
                            isInvoiceRefundable(invoice)
                        "
                        size="sm"
                        variant="outline-danger"
                        class="action-btn"
                        :disabled="creatingRefund"
                        @click="openCreateRefundModal(invoice)"
                      >
                        <feather-icon
                          icon="RotateCcwIcon"
                          size="14"
                          class="mr-1"
                        />
                        {{ $t("reception.create-refund-invoice") }}
                      </b-button>
                      <!-- Print button only for approved invoices -->
                      <b-button
                        v-if="invoice.status === 'approved'"
                        size="sm"
                        variant="primary"
                        class="action-btn"
                        @click="showInvoicePrint(invoice)"
                      >
                        <feather-icon
                          icon="PrinterIcon"
                          size="14"
                          class="mr-1"
                        />
                        {{ $t("reception.Print Invoice") }}
                      </b-button>
                    </div>
                  </b-card>
                </b-col>
              </b-row>
            </div>

            <template v-if="loading">
              <div>
                <div class="d-flex justify-content-center mb-1">
                  <b-spinner :label="$t('common.loading')" />
                </div>
              </div>
            </template>

            <div
              v-if="!loading && invoices.length === 0"
              class="text-center py-5"
            >
              <i
                class="fas fa-file-invoice"
                style="font-size: 3rem; opacity: 0.3"
              />
              <p class="text-muted mt-3">
                {{ $t("common.no-invoices-yet") }}
              </p>
            </div>
          </b-col>
        </b-row>
      </div>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span
              class="text-muted"
            >{{ $t("common.showing") }} {{ startRow }} {{ $t("common.to") }}
              {{ endRow }} {{ $t("common.of") }} {{ dataCount }}
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end flex-wrap gap-2"
          >
            <b-form-group class="mb-0 per-page-selector">
              <b-form-select
                v-model="take"
                :options="perPageOptions"
                size="sm"
                class="d-inline-block"
                style="width: auto; min-width: 80px"
              />
            </b-form-group>
            <b-pagination
              v-model="page"
              :total-rows="dataCount"
              :per-page="take"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="fetchInvoices"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Print Modal Confirmation -->
    <b-modal
      id="modal-confirm-print"
      :visible.sync="showPrintModal"
      :no-close-on-backdrop="true"
      :title="$t('reception.print-invoice')"
      ok-only
      :ok-title="$t('common.print')"
      @ok="handlePrintOk"
      @hide="handlePrintHide"
    >
      <b-card-text>
        <h5>{{ $t("reception.print-invoice") }}</h5>
        {{ $t("surgery.print-sure") }}
      </b-card-text>
    </b-modal>

    <!-- Create Refund Invoice Modal -->
    <b-modal
      id="modal-create-refund"
      :visible.sync="showCreateRefundModal"
      :title="$t('reception.create-refund-invoice')"
      :no-close-on-backdrop="creatingRefund"
      :no-close-on-esc="creatingRefund"
      @hide="handleRefundModalHide"
    >
      <div
        v-if="refundTargetInvoice"
        class="mb-2 p-2 rounded bg-light"
      >
        <p class="mb-1">
          <strong>{{ $t("common.invoice-number") }}:</strong>
          #{{ refundTargetInvoice.invoice_no }}
        </p>
        <p class="mb-1">
          <strong>{{ $t("common.patient") }}:</strong>
          {{
            getNestedProperty(
              refundTargetInvoice,
              "patient_card.patient.name"
            ) || "N/A"
          }}
        </p>
        <p class="mb-0">
          <strong>{{ $t("common.amount") }}:</strong>
          {{ formatPrice(refundTargetInvoice.amount) }}
        </p>
      </div>
      <div
        v-if="refundSummaryLoading"
        class="d-flex align-items-center mb-2"
      >
        <b-spinner
          small
          class="mr-1"
        />
        <span>{{ $t("reception.refund-summary-loading") }}</span>
      </div>
      <b-alert
        v-if="refundSummaryError"
        variant="danger"
        show
        class="mb-2"
      >
        {{ refundSummaryError }}
      </b-alert>
      <div
        v-if="refundSummaryData && !refundSummaryError"
        class="refund-summary-card mb-2"
      >
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h5 class="mb-0">
            {{ $t("reception.refund-summary-title") }}
          </h5>
          <b-badge
            :variant="
              ['approved', 'paid', 'printed'].includes(
                (refundSummaryData.invoice_status || '').toLowerCase()
              )
                ? 'success'
                : 'warning'
            "
          >
            {{
              refundSummaryData.invoice_status ||
                refundTargetInvoice.status ||
                "N/A"
            }}
          </b-badge>
        </div>
        <div class="summary-row">
          <span>{{ $t("reception.refund-original-amount") }}</span>
          <strong>
            {{
              formatPrice(
                refundSummaryData.original_amount || refundTargetInvoice.amount
              )
            }}
          </strong>
        </div>
        <div class="summary-row">
          <span>{{ $t("reception.refund-total-refunded") }}</span>
          <strong>
            {{ formatPrice(refundSummaryData.total_refunded || 0) }}
          </strong>
        </div>
        <div class="summary-row remaining">
          <span>{{ $t("reception.refund-remaining-amount") }}</span>
          <strong>{{
            formatPrice(refundSummaryData.refundable_amount || 0)
          }}</strong>
        </div>
        <b-alert
          v-if="refundSummaryData.refunds_count"
          variant="info"
          show
          class="mt-1 mb-0"
        >
          {{
            $t("reception.refund-summary-note", {
              count: refundSummaryData.refunds_count,
            })
          }}
        </b-alert>
        <div
          v-if="
            refundSummaryData.refunds && refundSummaryData.refunds.length > 0
          "
          class="refund-history mt-2"
        >
          <h6 class="text-muted">
            {{ $t("reception.refund-previous-refunds") }}
          </h6>
          <div
            v-for="refund in refundSummaryData.refunds"
            :key="refund.id"
            class="refund-history-item"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong>#{{ refund.invoice_no }}</strong>
                <span class="mx-1">•</span>
                <span>{{ refund.status }}</span>
              </div>
              <span>{{ formatPrice(refund.amount) }}</span>
            </div>
            <small class="text-muted d-block">
              {{ refund.date }}
              <span v-if="refund.description">- {{ refund.description }}</span>
            </small>
          </div>
        </div>
      </div>
      <b-form @submit.stop.prevent="submitRefundInvoice">
        <b-form-group
          :label="$t('accounting.refund-amount')"
          label-for="refund-amount-input"
        >
          <b-form-input
            id="refund-amount-input"
            v-model.number="refundForm.amount"
            type="number"
            min="0"
            :max="maxRefundableAmount || null"
            step="0.01"
            :state="refundErrors.amount ? false : null"
            :disabled="creatingRefund || !isRefundFormEnabled"
          />
          <small
            v-if="refundErrors.amount"
            class="text-danger d-block"
          >
            {{ refundErrors.amount }}
          </small>
        </b-form-group>

        <b-form-group
          :label="$t('accounting.refund-reason')"
          label-for="refund-reason-input"
        >
          <b-form-input
            id="refund-reason-input"
            v-model="refundForm.reason"
            :placeholder="$t('common.reason')"
            :state="refundErrors.reason ? false : null"
            :disabled="creatingRefund || !isRefundFormEnabled"
          />
          <small
            v-if="refundErrors.reason"
            class="text-danger d-block"
          >
            {{ refundErrors.reason }}
          </small>
        </b-form-group>

        <b-form-group
          :label="$t('common.description')"
          label-for="refund-desc"
        >
          <b-form-textarea
            id="refund-desc"
            v-model="refundForm.description"
            :placeholder="$t('reception.refund-description-placeholder')"
            :disabled="creatingRefund || !isRefundFormEnabled"
            rows="3"
          />
          <small
            v-if="refundErrors.description"
            class="text-danger d-block"
          >
            {{ refundErrors.description }}
          </small>
        </b-form-group>

        <b-form-group
          :label="$t('common.date')"
          label-for="refund-date-input"
        >
          <b-form-datepicker
            id="refund-date-input"
            v-model="refundForm.date"
            :state="refundErrors.date ? false : null"
            :disabled="creatingRefund || !isRefundFormEnabled"
          />
          <small
            v-if="refundErrors.date"
            class="text-danger d-block"
          >
            {{ refundErrors.date }}
          </small>
        </b-form-group>
      </b-form>
      <template #modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          :disabled="creatingRefund"
          @click="cancel()"
        >
          {{ $t("common.cancel") }}
        </b-button>
        <b-button
          variant="primary"
          class="ml-1"
          :disabled="!canSubmitRefund"
          @click="submitRefundInvoice"
        >
          <b-spinner
            v-if="creatingRefund"
            small
            type="grow"
            class="mr-50"
          />
          {{ $t("common.create") }}
        </b-button>
      </template>
    </b-modal>

    <!-- Print Invoice Components -->
    <!-- NICU Invoice -->
    <NICUInvoice
      v-if="
        currentInvoiceForPrint && currentInvoiceForPrint.type === 'invoice_nicu'
      "
      :key="getPrintComponentKey('nicu')"
      :patient-data="patientDataForPrint"
      :data="currentInvoiceForPrint"
      :incubator-name="incubatorNameForPrint"
    />

    <!-- Out Clinic Invoice -->
    <OutClinicInvoice
      v-if="
        currentInvoiceForPrint &&
          currentInvoiceForPrint.type === 'invoice_out_clinic'
      "
      :key="getPrintComponentKey('outclinic')"
      :patient-data="patientDataForPrint"
      :data="currentInvoiceForPrint"
    />

    <!-- Emergency Invoice -->
    <EmergencyInvoice
      v-if="
        currentInvoiceForPrint &&
          currentInvoiceForPrint.type === 'invoice_emergency'
      "
      :key="getPrintComponentKey('emergency')"
      :patient-data="patientDataForPrint"
      :data="currentInvoiceForPrint"
    />

    <!-- Test Invoice -->
    <TestInvoice
      v-if="
        currentInvoiceForPrint &&
          currentInvoiceForPrint.type === 'invoice_patient_card_test'
      "
      :key="getPrintComponentKey('test')"
      :patient-data="getPatientDataForTestInvoice()"
      :data="currentInvoiceForPrint"
    />

    <!-- Refund Invoice -->
    <RefundInvoice
      v-if="currentInvoiceForPrint && currentInvoiceForPrint.type === 'refund'"
      :key="getPrintComponentKey('refund')"
      :patient-data="patientDataForPrint"
      :data="currentInvoiceForPrint"
    />

    <!-- Surgery Insurance Invoice -->
    <SurgeryInsuranceInvoicePrint
      v-if="
        currentInvoiceForPrint &&
          currentInvoiceForPrint.type === 'invoice_insurance_surgery'
      "
      :key="getPrintComponentKey('surgery')"
      :patient-data="patientDataForPrint"
      :data="currentInvoiceForPrint"
    />

    <!-- Bulk Print Components - All Selected Invoices -->
    <div id="BulkPrintInvoices">
      <!-- NICU Invoice -->
      <NICUInvoice
        v-for="invoice in bulkPrintInvoicesData.filter(
          (inv) => inv.type === 'invoice_nicu'
        )"
        :key="`bulk-nicu-${invoice.id}`"
        :patient-data="getBulkPatientData(invoice)"
        :data="invoice"
        :incubator-name="getBulkIncubatorName(invoice)"
      />

      <!-- Out Clinic Invoice -->
      <OutClinicInvoice
        v-for="invoice in bulkPrintInvoicesData.filter(
          (inv) => inv.type === 'invoice_out_clinic'
        )"
        :key="`bulk-outclinic-${invoice.id}`"
        :patient-data="getBulkPatientData(invoice)"
        :data="invoice"
      />

      <!-- Emergency Invoice -->
      <EmergencyInvoice
        v-for="invoice in bulkPrintInvoicesData.filter(
          (inv) => inv.type === 'invoice_emergency'
        )"
        :key="`bulk-emergency-${invoice.id}`"
        :patient-data="getBulkPatientData(invoice)"
        :data="invoice"
      />

      <!-- Test Invoice -->
      <TestInvoice
        v-for="invoice in bulkPrintInvoicesData.filter(
          (inv) => inv.type === 'invoice_patient_card_test'
        )"
        :key="`bulk-test-${invoice.id}`"
        :patient-data="getBulkPatientDataForTest(invoice)"
        :data="invoice"
      />

      <!-- Refund Invoice -->
      <RefundInvoice
        v-for="invoice in bulkPrintInvoicesData.filter(
          (inv) => inv.type === 'refund'
        )"
        :key="`bulk-refund-${invoice.id}`"
        :patient-data="getBulkPatientData(invoice)"
        :data="invoice"
      />

      <!-- Surgery Insurance Invoice -->
      <SurgeryInsuranceInvoicePrint
        v-for="invoice in bulkPrintInvoicesData.filter(
          (inv) => inv.type === 'invoice_insurance_surgery'
        )"
        :key="`bulk-surgery-${invoice.id}`"
        :patient-data="getBulkPatientData(invoice)"
        :data="invoice"
      />
    </div>

    <!-- Hidden print buttons for invoices -->
    <b-button
      v-if="
        currentInvoiceForPrint && currentInvoiceForPrint.type === 'invoice_nicu'
      "
      :key="getPrintComponentKey('btn-nicu')"
      ref="NICUInvoiceRef"
      v-print="NICUInvoiceConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <b-button
      v-if="
        currentInvoiceForPrint &&
          currentInvoiceForPrint.type === 'invoice_out_clinic'
      "
      :key="getPrintComponentKey('btn-outclinic')"
      ref="OutClinicInvoiceRef"
      v-print="OutClinicInvoiceConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <b-button
      v-if="
        currentInvoiceForPrint &&
          currentInvoiceForPrint.type === 'invoice_emergency'
      "
      :key="getPrintComponentKey('btn-emergency')"
      ref="EmergencyInvoiceRef"
      v-print="EmergencyInvoiceConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <b-button
      v-if="
        currentInvoiceForPrint &&
          currentInvoiceForPrint.type === 'invoice_patient_card_test'
      "
      :key="getPrintComponentKey('btn-test')"
      ref="TestInvoiceRef"
      v-print="TestInvoiceConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <b-button
      v-if="currentInvoiceForPrint && currentInvoiceForPrint.type === 'refund'"
      :key="getPrintComponentKey('btn-refund')"
      ref="RefundInvoiceRef"
      v-print="RefundInvoiceConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <b-button
      v-if="
        currentInvoiceForPrint &&
          currentInvoiceForPrint.type === 'invoice_insurance_surgery'
      "
      :key="getPrintComponentKey('btn-surgery')"
      ref="SurgeryInsuranceInvoiceRef"
      v-print="SurgeryInsuranceInvoiceConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <!-- Hidden print button for bulk printing -->
    <b-button
      v-if="bulkPrintInvoicesData.length > 0"
      ref="BulkPrintRef"
      v-print="BulkPrintConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("common.print-selected") }}
    </b-button>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BTable,
  BButton,
  BButtonGroup,
  BForm,
  BFormGroup,
  BFormDatepicker,
  BFormSelect,
  BPagination,
  BSpinner,
  BIconSearch,
  BFormInput,
  BBadge,
  BFormTextarea,
  BAlert,
  BFormCheckbox,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import CustomChangeInvoiceStatusDropDown from '@/components/invoices/CustomChangeInvoiceStatusDropDown.vue'
import $http from '@/plugins/axios'

const NICUInvoice = () => import('@/components/printables/NICUInvoice.vue')
const OutClinicInvoice = () => import('@/components/printables/OutClinicInvoice.vue')
const EmergencyInvoice = () => import('@/components/printables/EmergencyInvoice.vue')
const TestInvoice = () => import('@/components/printables/TestInvoice.vue')
const RefundInvoice = () => import('@/components/printables/RefundInvoice.vue')
const SurgeryInsuranceInvoicePrint = () => import('@/components/printables/SurgeryInsuranceInvoice.vue')

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTable,
    BButton,
    BButtonGroup,
    BForm,
    BFormGroup,
    BFormDatepicker,
    BFormSelect,
    BPagination,
    BSpinner,
    BIconSearch,
    BBadge,
    BFormInput,
    BFormTextarea,
    BAlert,
    BFormCheckbox,
    vSelect,
    ToastificationContent,
    CustomChangeInvoiceStatusDropDown,
    NICUInvoice,
    OutClinicInvoice,
    EmergencyInvoice,
    TestInvoice,
    RefundInvoice,
    SurgeryInsuranceInvoicePrint,
  },
  data() {
    return {
      viewMode: localStorage.getItem('cashierInvoicesView') || 'cards',
      activeStatusTab: 'pending',
      filters: {
        status: 'pending',
        patientName: '',
        invoiceNumber: '',
        date: null,
        invoiceType: null,
      },
      invoices: [],
      page: 1,
      take: 10,
      perPageOptions: [5, 10, 15, 25, 50, 100, 200],
      dataCount: 0,
      loading: false,
      currentInvoiceForPrint: null,
      patientDataForPrint: null,
      incubatorNameForPrint: null,
      NICUInvoiceConfig: {
        id: 'NICUInvoice',
      },
      OutClinicInvoiceConfig: {
        id: 'OutClinicInvoice',
      },
      EmergencyInvoiceConfig: {
        id: 'EmergencyInvoice',
      },
      TestInvoiceConfig: {
        id: 'TestInvoice',
      },
      RefundInvoiceConfig: {
        id: 'RefundInvoice',
      },
      SurgeryInsuranceInvoiceConfig: {
        id: 'SurgeryInsuranceInvoice',
      },
      BulkPrintConfig: {
        id: 'BulkPrintInvoices',
      },
      bulkPrintInvoicesData: [],
      printLoading: false,
      printKey: 0,
      showPrintModal: false,
      showCreateRefundModal: false,
      creatingRefund: false,
      refundTargetInvoice: null,
      refundForm: {
        original_invoice_id: null,
        amount: null,
        reason: '',
        description: '',
        date: new Date().toISOString().slice(0, 10),
      },
      refundErrors: {},
      refundSummaryLoading: false,
      refundSummaryData: null,
      refundSummaryError: null,
      selectedInvoices: [],
      bulkUpdating: false,
      bulkPrintQueue: [],
      currentBulkPrintIndex: 0,
    }
  },
  computed: {
    startRow() {
      return this.dataCount === 0 ? 0 : (this.page - 1) * this.take + 1
    },
    endRow() {
      const end = this.page * this.take
      return end > this.dataCount ? this.dataCount : end
    },
    statusOptions() {
      return [
        { label: this.$t('common.pending'), value: 'pending' },
        { label: this.$t('common.approved'), value: 'approved' },
        { label: this.$t('common.printed'), value: 'printed' },
        { label: this.$t('common.rejected'), value: 'rejected' },
      ]
    },
    bulkStatusOptions() {
      return [{ label: this.$t('common.approved'), value: 'approved' }]
    },
    invoiceTypeOptions() {
      const typeMap = {
        invoice_out_clinic: 'cashiers.invoice-type-out-clinic',
        invoice_nicu: 'cashiers.invoice-type-nicu',
        invoice_patient_card_test: 'cashiers.invoice-type-patient-card-test',
        refund: 'cashiers.invoice-type-refund',
        invoice_insurance_surgery: 'cashiers.invoice-type-insurance-surgery',
        invoice_emergency: 'cashiers.invoice-type-emergency',
      }
      return Object.keys(typeMap).map(key => ({
        value: key,
        label: this.$t(typeMap[key]),
      }))
    },
    tableColumns() {
      return [
        {
          key: 'select',
          label: '',
          thClass: 'text-center',
          tdClass: 'text-center',
          width: '50px',
        },
        {
          key: 'surgery_or_service',
          label: this.$t('surgery.surgery-or-service') || 'Surgery/Service',
          sortable: false,
        },
        {
          key: 'invoice_no',
          label: this.$t('common.invoice-number'),
          sortable: true,
        },
        {
          key: 'patient_name',
          label: this.$t('common.patient'),
          sortable: true,
        },
        { key: 'amount', label: this.$t('common.amount'), sortable: true },
        { key: 'type', label: this.$t('common.type'), sortable: true },
        { key: 'status', label: this.$t('common.status'), sortable: true },
        { key: 'date', label: this.$t('common.date'), sortable: true },
        { key: 'actions', label: this.$t('common.actions') },
      ]
    },
    permissionsList() {
      const permissions = this.$store?.state?.app?.permissions
      return Array.isArray(permissions) ? permissions : []
    },
    canCreateRefundInvoices() {
      return this.permissionsList.includes('store-invoice')
    },
    maxRefundableAmount() {
      if (
        this.refundSummaryData
        && this.refundSummaryData.refundable_amount !== undefined
      ) {
        const amount = Number(this.refundSummaryData.refundable_amount)
        return Number.isNaN(amount) ? 0 : amount
      }
      if (this.refundTargetInvoice) {
        const amount = Number(this.refundTargetInvoice.amount)
        return Number.isNaN(amount) ? 0 : amount
      }
      return 0
    },
    isRefundFormEnabled() {
      return (
        !this.refundSummaryLoading
        && !!this.refundSummaryData
        && this.refundSummaryData.can_be_refunded
      )
    },
    canSubmitRefund() {
      return (
        this.isRefundFormEnabled
        && !this.creatingRefund
        && !!this.refundForm.amount
        && Number(this.refundForm.amount) > 0
      )
    },
    selectedInvoicesTotal() {
      return this.selectedInvoices.reduce((total, invoiceId) => {
        const invoice = this.invoices.find(inv => inv.id === invoiceId)
        return total + (invoice ? Number(invoice.amount) || 0 : 0)
      }, 0)
    },
    canBulkPrint() {
      return (
        this.selectedInvoices.length > 0
        && this.selectedInvoices.every(id => {
          const invoice = this.invoices.find(inv => inv.id === id)
          return invoice && invoice.status === 'approved'
        })
      )
    },
    areAllInvoicesSelected() {
      return (
        this.invoices.length > 0
        && this.invoices.every(inv => this.selectedInvoices.includes(inv.id))
      )
    },
    areSomeInvoicesSelected() {
      return this.selectedInvoices.length > 0 && !this.areAllInvoicesSelected
    },
  },
  watch: {
    viewMode(val) {
      localStorage.setItem('cashierInvoicesView', val)
    },
    take(newValue, oldValue) {
      if (oldValue !== undefined && newValue !== oldValue) {
        this.page = 1
        this.fetchInvoices()
      }
    },
  },
  mounted() {
    // Check if there's a pending auto-print after page reload
    this.checkPendingAutoPrint()
  },
  methods: {
    getNestedProperty(obj, path, defaultValue = 'N/A') {
      const result = path
        .split('.')
        .reduce(
          (acc, part) => (acc && acc[part] ? acc[part] : defaultValue),
          obj,
        )
      return result
    },
    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : '0 IQD'
    },
    getInvoiceTypeName(type) {
      const typeMap = {
        invoice_out_clinic: 'cashiers.invoice-type-out-clinic',
        invoice_nicu: 'cashiers.invoice-type-nicu',
        invoice_patient_card_test: 'cashiers.invoice-type-patient-card-test',
        refund: 'cashiers.invoice-type-refund',
        invoice_insurance_surgery: 'cashiers.invoice-type-insurance-surgery',
        invoice_emergency: 'cashiers.invoice-type-emergency',
      }
      const translationKey = typeMap[type]
      return translationKey ? this.$t(translationKey) : type
    },
    getSurgeryOrServiceTitle(invoice) {
      // Try to get surgery title first
      if (invoice.surgery && invoice.surgery.title) {
        return invoice.surgery.title
      }
      // Try to get service title
      if (invoice.service && invoice.service.title) {
        return invoice.service.title
      }
      // Fallback to invoice type name
      return this.getInvoiceTypeName(invoice.type)
    },
    handleStatusTabChange(status) {
      this.filters.status = status
      this.activeStatusTab = status
      this.page = 1
      this.fetchInvoices()
    },
    resetFilters() {
      this.filters = {
        status: 'pending',
        patientName: '',
        invoiceNumber: '',
        date: null,
        invoiceType: null,
      }
      this.activeStatusTab = 'pending'
      this.page = 1
      this.fetchInvoices()
    },
    async fetchInvoices() {
      this.loading = true
      try {
        let url = `/invoices?include=patientCard,service,surgery&page=${this.page}&take=${this.take}&sort=-id`

        // Add status filter - send both status and internal_status for approved, and only internal_status for printed
        if (this.filters.status) {
          if (this.filters.status === 'printed') {
            // For printed tab, only filter by internal_status = printed
            url += '&filter[internal_status]=printed'
          } else {
            // Always send the status filter for other tabs
            url += `&filter[status]=${this.filters.status}`

            // For approved tab, also filter by internal_status = pending
            if (this.filters.status === 'approved') {
              url += '&filter[internal_status]=pending'
            }
          }
        }

        // Handle patient name filter - need to get patient card IDs first
        let patientCardIds = null
        if (this.filters.patientName && this.filters.patientName.trim()) {
          try {
            // First, search for patient cards by patient name
            const patientName = encodeURIComponent(
              this.filters.patientName.trim(),
            )
            const patientCardsUrl = `/patientCard?filter[patient.name]=${patientName}&take=1000`
            await this.getList(patientCardsUrl)
            const patientCards = Array.isArray(this.all_data?.data)
              ? this.all_data.data
              : []
            patientCardIds = patientCards
              .map(card => card.id)
              .filter(Boolean)

            // If no patient cards found, set empty array to return no results
            if (patientCardIds.length === 0) {
              this.invoices = []
              this.dataCount = 0
              this.loading = false
              return
            }

            // Filter invoices by patient card IDs
            url += `&filter[patient_card_id]=${patientCardIds.join(',')}`
          } catch (error) {
            console.error('Error fetching patient cards by name:', error)
            // If error, continue without patient name filter
          }
        }

        // Add invoice number filter
        if (this.filters.invoiceNumber && this.filters.invoiceNumber.trim()) {
          url += `&filter[invoice_no]=${this.filters.invoiceNumber.trim()}`
        }

        // Add date filter
        if (this.filters.date) {
          url += `&filter[date]=${this.filters.date}`
        }

        if (this.filters.invoiceType) {
          url += `&filter[type]=${this.filters.invoiceType}`
        }

        await this.getList(url)
        this.dataCount = this.all_data?.total || 0
        this.invoices = Array.isArray(this.all_data?.data)
          ? this.all_data.data
          : []

        // Fetch patient data separately for invoices that have patientCard but no patient data
        const invoicesNeedingPatient = this.invoices.filter(
          inv => inv.patient_card?.id && !inv.patient_card?.patient,
        )

        if (invoicesNeedingPatient.length > 0) {
          const patientCardIdsForPatientData = invoicesNeedingPatient
            .map(inv => inv.patient_card.id)
            .filter((id, index, self) => self.indexOf(id) === index)

          if (patientCardIdsForPatientData.length > 0) {
            try {
              // Fetch patient cards with patient data in batches if needed
              const batchSize = 50
              const patientCardsMap = new Map()

              for (
                let i = 0;
                i < patientCardIdsForPatientData.length;
                i += batchSize
              ) {
                const batch = patientCardIdsForPatientData.slice(
                  i,
                  i + batchSize,
                )
                const patientCardsUrl = `/patientCard?filter[id]=${batch.join(
                  ',',
                )}&include=patient`
                await this.getList(patientCardsUrl)
                if (this.all_data?.data) {
                  this.all_data.data.forEach(card => {
                    if (card.id && card.patient) {
                      patientCardsMap.set(card.id, card)
                    }
                  })
                }
              }

              // Attach patient data to invoices
              this.invoices = this.invoices.map(invoice => {
                if (
                  invoice.patient_card?.id
                  && !invoice.patient_card?.patient
                ) {
                  const patientCard = patientCardsMap.get(
                    invoice.patient_card.id,
                  )
                  if (patientCard && patientCard.patient) {
                    invoice.patient_card.patient = patientCard.patient
                  }
                }
                return invoice
              })
            } catch (error) {
              console.error('Error fetching patient data:', error)
              // Continue without patient data
            }
          }
        }
      } catch (error) {
        console.error('Error fetching invoices:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-fetching-data'),
          },
        })
        this.invoices = []
        this.dataCount = 0
      } finally {
        this.loading = false
      }
    },
    handleStatusChangeRequest(status, invoiceId) {
      // Store invoice ID in localStorage if changing to approved
      if (status === 'approved') {
        localStorage.setItem(
          'pendingAutoPrintInvoice',
          JSON.stringify({ invoiceId, timestamp: Date.now() }),
        )
      }
    },
    async approveInvoice(invoice) {
      try {
        this.loading = true
        const params = {
          form: { status: 'approved' },
          path: `/invoices/${invoice.id}/changeStatus`,
        }
        await this.$store.dispatch('put', params)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.invoice-approved'),
          },
        })
        await this.fetchInvoices()
      } catch (error) {
        console.error('Error approving invoice:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-occurred'),
          },
        })
      } finally {
        this.loading = false
      }
    },
    async approveAndPrintInvoice(invoice) {
      try {
        this.loading = true
        this.printLoading = true

        // First, approve the invoice
        const approveParams = {
          form: { status: 'approved' },
          path: `/invoices/${invoice.id}/changeStatus`,
        }
        const approveResponse = await this.$store.dispatch(
          'put',
          approveParams,
        )

        // Show success message
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text:
              this.$t('common.invoice-approved')
              || 'Invoice approved successfully',
          },
        })

        // Create updated invoice object with approved status
        const updatedInvoice = {
          ...invoice,
          status: 'approved',
        }

        // Refresh the list in the background
        this.fetchInvoices().catch(() => {
          // Ignore errors in background refresh
        })

        // Now print the invoice directly without waiting for list refresh
        await this.autoPrintInvoice(updatedInvoice)
      } catch (error) {
        console.error('Error approving and printing invoice:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text:
              error.response?.data?.message || this.$t('common.error-occurred'),
          },
        })
      } finally {
        this.loading = false
        this.printLoading = false
      }
    },
    async handleStatusChange(invoice) {
      // This handler runs after status change, but page reloads immediately
      // So we rely on checkPendingAutoPrint after reload
      // Just refresh the list if page doesn't reload
      try {
        await this.fetchInvoices()
      } catch (error) {
        console.error('Error refreshing invoices:', error)
      }
    },
    async checkPendingAutoPrint() {
      // Check if there's a pending auto-print after page reload
      const pendingPrint = localStorage.getItem('pendingAutoPrintInvoice')
      if (pendingPrint) {
        try {
          const { invoiceId, timestamp } = JSON.parse(pendingPrint)

          // Only process if it's recent (within last 10 seconds)
          if (Date.now() - timestamp > 10000) {
            localStorage.removeItem('pendingAutoPrintInvoice')
            await this.fetchInvoices()
            return
          }

          // Wait for invoices to load
          await this.fetchInvoices()

          // Find the invoice
          const invoice = this.invoices.find(inv => inv.id === invoiceId)

          if (invoice && invoice.status === 'approved') {
            // Clear the flag
            localStorage.removeItem('pendingAutoPrintInvoice')
            // Wait a bit for UI to settle, then auto-print
            await this.$nextTick()
            setTimeout(() => {
              console.log('Auto-printing invoice:', invoice.id, invoice)
              this.autoPrintInvoice(invoice)
            }, 2000)
          } else {
            // Clear the flag if invoice not found or status not approved
            localStorage.removeItem('pendingAutoPrintInvoice')
          }
        } catch (error) {
          console.error('Error checking pending auto-print:', error)
          localStorage.removeItem('pendingAutoPrintInvoice')
        }
      } else {
        // If no pending print, just fetch invoices normally
        await this.fetchInvoices()
      }
    },
    async autoPrintInvoice(invoice) {
      if (this.printLoading) return
      this.currentInvoiceForPrint = invoice
      // Skip the confirmation modal and print directly
      try {
        this.printLoading = true
        this.loading = true

        let includeString = 'service,surgery,patientCard,doctor,invoiceItems'

        if (invoice.type === 'invoice_nicu') {
          includeString
            += ',patientCardIncubator,patientCardIncubator.incubator'
        } else if (invoice.type === 'invoice_patient_card_test') {
          includeString += ',patientCardTest,patientCardTest.test'
        }

        const url = `/invoices?filter[id]=${invoice.id}&include=${includeString}`
        await this.getList(url)

        if (
          this.all_data
          && this.all_data.data
          && this.all_data.data.length > 0
        ) {
          this.currentInvoiceForPrint = this.all_data.data[0]

          // Set patient data for print
          if (this.currentInvoiceForPrint.patient_card) {
            this.patientDataForPrint = {
              ...this.currentInvoiceForPrint.patient_card,
              patient: this.currentInvoiceForPrint.patient_card.patient
                ? { ...this.currentInvoiceForPrint.patient_card.patient }
                : null,
            }
          }

          // Get incubator name for NICU invoices
          if (this.currentInvoiceForPrint.type === 'invoice_nicu') {
            const incubator = this.currentInvoiceForPrint.patient_card_incubator
            this.incubatorNameForPrint = incubator && incubator.incubator
              ? incubator.incubator.name
              : 'N/A'
          }

          // Wait for print components to render - use multiple nextTick and force update
          await this.$nextTick()
          await this.$forceUpdate()
          await this.$nextTick()
          await this.$nextTick()

          // Give more time for components to fully render
          setTimeout(() => {
            const refName = this.getPrintRefName()
            console.log('Attempting to print, refName:', refName)
            console.log('Invoice type:', this.currentInvoiceForPrint.type)
            console.log('Available refs:', Object.keys(this.$refs))
            console.log(
              'Current invoice for print:',
              this.currentInvoiceForPrint,
            )

            if (refName) {
              const printRef = this.$refs[refName]
              console.log('Print ref:', printRef)
              if (printRef) {
                // Handle both array and single ref
                const button = Array.isArray(printRef) ? printRef[0] : printRef
                if (button && button.$el) {
                  console.log('Clicking print button via $el...')
                  button.$el.click()
                } else if (button && typeof button.click === 'function') {
                  console.log('Clicking print button...')
                  button.click()
                } else if (button) {
                  console.log('Triggering click event on button element...')
                  const element = button.$el || button
                  if (element) {
                    element.click()
                  }
                } else {
                  console.error('Print button not accessible')
                }
                // Change status to printed after print dialog opens
                setTimeout(() => {
                  this.changeStatusToPrintedInvoice()
                }, 2000)
              } else {
                console.error('Print ref not found:', refName)
                // Try alternative: use window.print() as fallback
                console.log('Using window.print() as fallback')
                window.print()
                setTimeout(() => {
                  this.changeStatusToPrintedInvoice()
                }, 1000)
              }
            } else {
              console.error(
                'No ref name found for invoice type:',
                this.currentInvoiceForPrint.type,
              )
              // Fallback: use window.print()
              console.log('Using window.print() as fallback')
              window.print()
              setTimeout(() => {
                this.changeStatusToPrintedInvoice()
              }, 1000)
            }
          }, 1200)
        }
      } catch (error) {
        console.error('Failed to auto-print invoice:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-occurred'),
          },
        })
      } finally {
        this.loading = false
        this.printLoading = false
      }
    },
    async showInvoicePrint(invoice) {
      if (this.printLoading) return
      // Increment print key to force re-render of print components
      this.printKey = Date.now()
      // Reset print state to force re-render
      this.currentInvoiceForPrint = null
      this.patientDataForPrint = null
      this.incubatorNameForPrint = null
      await this.$nextTick()
      // Set the invoice after reset
      this.currentInvoiceForPrint = invoice
      await this.$nextTick()
      // Show the print modal
      this.showPrintModal = true
    },
    handlePrintOk(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.printInvoice()
    },
    handlePrintHide() {
      this.printLoading = false
      this.showPrintModal = false
    },
    async printInvoice() {
      try {
        this.printLoading = true
        this.loading = true

        let includeString = 'service,surgery,patientCard,doctor,invoiceItems'

        if (this.currentInvoiceForPrint.type === 'invoice_nicu') {
          includeString
            += ',patientCardIncubator,patientCardIncubator.incubator'
        } else if (
          this.currentInvoiceForPrint.type === 'invoice_patient_card_test'
        ) {
          includeString += ',patientCardTest,patientCardTest.test'
        }

        const url = `/invoices?filter[id]=${this.currentInvoiceForPrint.id}&include=${includeString}`
        await this.getList(url)

        if (
          this.all_data
          && this.all_data.data
          && this.all_data.data.length > 0
        ) {
          this.currentInvoiceForPrint = this.all_data.data[0]

          // Set patient data for print
          if (this.currentInvoiceForPrint.patient_card) {
            this.patientDataForPrint = {
              ...this.currentInvoiceForPrint.patient_card,
              patient: this.currentInvoiceForPrint.patient_card.patient
                ? { ...this.currentInvoiceForPrint.patient_card.patient }
                : null,
            }
          }

          // Get incubator name for NICU invoices
          if (this.currentInvoiceForPrint.type === 'invoice_nicu') {
            const incubator = this.currentInvoiceForPrint.patient_card_incubator
            this.incubatorNameForPrint = incubator && incubator.incubator
              ? incubator.incubator.name
              : 'N/A'
          }

          await this.$nextTick()
          await this.$nextTick()
          await this.$nextTick()

          setTimeout(() => {
            const refName = this.getPrintRefName()
            if (refName && this.$refs[refName]) {
              // Handle both array and single ref
              const button = Array.isArray(this.$refs[refName])
                ? this.$refs[refName][0]
                : this.$refs[refName]
              if (button) {
                button.click()
                // Change status to printed after print dialog opens
                setTimeout(() => {
                  this.changeStatusToPrintedInvoice()
                }, 1500)
              }
            } else {
              // Fallback: use window.print()
              window.print()
              setTimeout(() => {
                this.changeStatusToPrintedInvoice()
              }, 1000)
            }
            // Close the print modal
            this.showPrintModal = false
          }, 800)
        }
      } catch (error) {
        console.error('Failed to print invoice:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-occurred'),
          },
        })
      } finally {
        this.loading = false
        this.printLoading = false
      }
    },
    triggerPrint() {
      const refName = this.getPrintRefName()
      if (refName && this.$refs[refName]) {
        // Handle both array and single ref
        if (Array.isArray(this.$refs[refName]) && this.$refs[refName][0]) {
          this.$refs[refName][0].click()
        } else {
          this.$refs[refName].click()
        }
      }
    },
    getPrintRefName() {
      if (!this.currentInvoiceForPrint) return null
      const typeMap = {
        invoice_nicu: 'NICUInvoiceRef',
        invoice_out_clinic: 'OutClinicInvoiceRef',
        invoice_emergency: 'EmergencyInvoiceRef',
        invoice_patient_card_test: 'TestInvoiceRef',
        refund: 'RefundInvoiceRef',
        invoice_insurance_surgery: 'SurgeryInsuranceInvoiceRef',
      }
      return typeMap[this.currentInvoiceForPrint.type]
    },
    getPatientDataForTestInvoice() {
      if (
        this.currentInvoiceForPrint
        && this.currentInvoiceForPrint.patient_card
        && this.currentInvoiceForPrint.patient_card.patient
      ) {
        return { ...this.currentInvoiceForPrint.patient_card.patient }
      }
      return {}
    },
    getPrintComponentKey(prefix) {
      const invoiceId = this.currentInvoiceForPrint
        ? this.currentInvoiceForPrint.id
        : 'none'
      return `${prefix}-${invoiceId}-${this.printKey}`
    },
    changeStatusToPrintedInvoice() {
      const params = {
        form: { status: 'printed' },
        path: `/invoices/${this.currentInvoiceForPrint.id}/changeStatus`,
      }
      this.$store
        .dispatch('put', params)
        .then(response => {
          this.fetchInvoices()
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.success'),
              variant: 'success',
              text: response.data.message,
            },
          })
        })
        .catch(error => {
          if (error.response && error.response.status === 400) {
            for (const item in error.response.data) {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'validation error',
                  variant: 'danger',
                  text: error.response.data[item][0],
                },
              })
            }
          }
        })
    },
    async openCreateRefundModal(invoice) {
      // Extra safety: only allow refund modal if user has permission and invoice is refundable
      if (!this.canCreateRefundInvoices || !invoice || !this.isInvoiceRefundable(invoice)) return
      this.refundTargetInvoice = invoice
      this.refundForm = {
        original_invoice_id: invoice.id,
        amount: Number(invoice.amount) || null,
        reason: '',
        description: '',
        date: new Date().toISOString().slice(0, 10),
      }
      this.refundErrors = {}
      this.refundSummaryData = null
      this.refundSummaryError = null
      this.refundSummaryLoading = true
      this.showCreateRefundModal = true
      await this.loadRefundSummary(invoice)
    },
    handleRefundModalHide() {
      if (this.creatingRefund) return
      this.resetRefundState()
    },
    isInvoiceRefundable(invoice) {
      if (!invoice || !invoice.status) return false
      const status = String(invoice.status).toLowerCase()
      // Not refundable when status is pending or refund; refundable otherwise
      return !['pending', 'refund', 'rejected'].includes(status)
    },
    getDefaultRefundForm() {
      return {
        original_invoice_id: null,
        amount: null,
        reason: '',
        description: '',
        date: new Date().toISOString().slice(0, 10),
      }
    },
    resetRefundState() {
      this.showCreateRefundModal = false
      this.refundTargetInvoice = null
      this.refundErrors = {}
      this.refundForm = this.getDefaultRefundForm()
      this.refundSummaryData = null
      this.refundSummaryError = null
      this.refundSummaryLoading = false
    },
    validateRefundForm() {
      const errors = {}
      if (!this.refundForm.original_invoice_id) {
        errors.original_invoice_id = this.$t(
          'reception.select-invoice-before-refund',
        )
      }
      const maxAmount = this.maxRefundableAmount
        || (this.refundTargetInvoice
          ? Number(this.refundTargetInvoice.amount) || 0
          : null)
      if (
        this.refundForm.amount === null
        || this.refundForm.amount === ''
        || Number(this.refundForm.amount) <= 0
      ) {
        errors.amount = this.$t('reception.refund-amount-required')
      } else if (
        maxAmount !== null
        && Number(this.refundForm.amount) > maxAmount
      ) {
        errors.amount = this.$t('reception.refund-amount-exceeds')
      } else if (!this.refundSummaryLoading && !this.isRefundFormEnabled) {
        errors.amount = errors.amount || this.$t('reception.refund-not-allowed')
      }
      if (!this.refundForm.reason || !this.refundForm.reason.trim()) {
        errors.reason = this.$t('reception.refund-reason-required')
      }
      if (!this.refundForm.date) {
        errors.date = this.$t('reception.refund-date-required')
      }
      this.refundErrors = errors
      return Object.keys(errors).length === 0
    },
    async loadRefundSummary(invoice) {
      if (!invoice || !invoice.id) {
        this.refundSummaryLoading = false
        this.refundSummaryError = this.$t('reception.refund-summary-failed')
        return
      }
      try {
        const response = await $http.get(
          `/invoices/${invoice.id}/refund-summary`,
        )
        const summary = response.data?.data || null
        this.refundSummaryData = summary
        if (summary) {
          const summaryStatus = summary.invoice_status || invoice.status || ''
          const normalizedStatus = summaryStatus
            ? summaryStatus.toLowerCase()
            : ''
          if (['pending', 'refund', 'rejected'].includes(normalizedStatus)) {
            this.refundSummaryError = this.$t('reception.refund-status-error', {
              status: summary.invoice_status || invoice.status || 'N/A',
            })
          } else if (
            summary.refundable_amount === undefined
            || Number(summary.refundable_amount) <= 0
            || summary.can_be_refunded === false
          ) {
            this.refundSummaryError = summary.refundable_amount === 0
              ? this.$t('reception.refund-no-amount-left')
              : this.$t('reception.refund-not-allowed')
          }

          const suggestedAmount = Number(summary.refundable_amount)
          if (!Number.isNaN(suggestedAmount) && suggestedAmount > 0) {
            this.refundForm.amount = suggestedAmount
          } else if (this.refundForm.amount) {
            this.refundForm.amount = Number(this.refundForm.amount)
          } else {
            this.refundForm.amount = null
          }
        }
      } catch (error) {
        console.error('Failed to load refund summary:', error)
        this.refundSummaryError = error.response?.data?.message
          || this.$t('reception.refund-summary-failed')
      } finally {
        this.refundSummaryLoading = false
      }
    },
    async submitRefundInvoice() {
      if (this.creatingRefund) return
      if (!this.validateRefundForm()) return
      if (!this.isRefundFormEnabled) return
      this.creatingRefund = true
      try {
        const payload = {
          original_invoice_id: this.refundForm.original_invoice_id,
          amount: Number(this.refundForm.amount),
          reason: this.refundForm.reason.trim(),
          description: this.refundForm.description
            ? this.refundForm.description.trim()
            : null,
          date: this.refundForm.date,
        }
        const params = {
          path: '/invoices/refund',
          form: payload,
        }
        await this.$store.dispatch('create', params)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('reception.refund-created-successfully'),
          },
        })
        this.resetRefundState()
        await this.fetchInvoices()
      } catch (error) {
        const responseErrors = error.response?.data?.errors || error.response?.data
        if (responseErrors && typeof responseErrors === 'object') {
          const normalizedErrors = { ...this.refundErrors };
          [
            'original_invoice_id',
            'amount',
            'reason',
            'description',
            'date',
          ].forEach(field => {
            if (responseErrors[field]) {
              normalizedErrors[field] = Array.isArray(responseErrors[field])
                ? responseErrors[field][0]
                : responseErrors[field]
            }
          })
          this.refundErrors = normalizedErrors
        }
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text:
              error.response?.data?.message
              || Object.values(this.refundErrors)[0]
              || this.$t('common.error-occurred'),
          },
        })
      } finally {
        this.creatingRefund = false
      }
    },
    // Bulk Selection Methods
    isInvoiceSelected(invoiceId) {
      return this.selectedInvoices.includes(invoiceId)
    },
    toggleInvoiceSelection(invoiceId, event) {
      if (event) {
        if (!this.selectedInvoices.includes(invoiceId)) {
          this.selectedInvoices.push(invoiceId)
        }
      } else {
        this.selectedInvoices = this.selectedInvoices.filter(
          id => id !== invoiceId,
        )
      }
    },
    clearSelection() {
      this.selectedInvoices = []
    },
    toggleSelectAll(event) {
      if (event) {
        // Select all
        this.selectedInvoices = this.invoices.map(inv => inv.id)
      } else {
        // Deselect all
        this.selectedInvoices = []
      }
    },
    async bulkUpdateStatus(status) {
      if (this.selectedInvoices.length === 0 || this.bulkUpdating) return

      this.bulkUpdating = true
      try {
        const invoicesPayload = this.selectedInvoices.map(id => ({
          id,
          status,
        }))

        const response = await $http.post('/invoices/bulkUpdateStatus', {
          invoices: invoicesPayload,
        })

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.status-updated-successfully'),
          },
        })

        this.clearSelection()
        await this.fetchInvoices()
      } catch (error) {
        console.error('Error bulk updating status:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text:
              error.response?.data?.message || this.$t('common.error-occurred'),
          },
        })
      } finally {
        this.bulkUpdating = false
      }
    },
    async bulkPrintInvoices() {
      if (this.selectedInvoices.length === 0 || !this.canBulkPrint) return

      const invoicesToPrint = this.invoices.filter(inv => this.selectedInvoices.includes(inv.id))

      if (invoicesToPrint.length === 0) return

      this.bulkPrintInvoicesData = []
      this.printLoading = true

      try {
        // Fetch all selected invoices with full data
        const invoiceIds = invoicesToPrint.map(inv => inv.id).join(',')
        let includeString = 'service,surgery,patientCard,doctor,invoiceItems'

        // Check if any invoice is NICU or Test type
        const hasNICU = invoicesToPrint.some(
          inv => inv.type === 'invoice_nicu',
        )
        const hasTest = invoicesToPrint.some(
          inv => inv.type === 'invoice_patient_card_test',
        )

        if (hasNICU) {
          includeString
            += ',patientCardIncubator,patientCardIncubator.incubator'
        }
        if (hasTest) {
          includeString += ',patientCardTest,patientCardTest.test'
        }

        // Build filter for multiple IDs - API accepts comma-separated values
        const url = `/invoices?filter[id]=${invoiceIds}&include=${includeString}&take=1000`
        await this.getList(url)

        if (
          this.all_data
          && this.all_data.data
          && this.all_data.data.length > 0
        ) {
          this.bulkPrintInvoicesData = this.all_data.data

          await this.$nextTick()
          await this.$nextTick()

          // Trigger print after a short delay
          setTimeout(() => {
            if (this.$refs.BulkPrintRef) {
              this.$refs.BulkPrintRef.click()
            } else {
              window.print()
            }

            // Update all invoices to printed status after print is triggered
            setTimeout(() => {
              this.updateBulkPrintStatus()
            }, 1000)
          }, 500)
        }
      } catch (error) {
        console.error('Error fetching invoices for bulk print:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-occurred'),
          },
        })
      } finally {
        this.printLoading = false
      }
    },
    async updateBulkPrintStatus() {
      if (this.selectedInvoices.length === 0) return

      try {
        const invoicesPayload = this.selectedInvoices.map(id => ({
          id,
          status: 'printed',
        }))

        await $http.post('/invoices/bulkUpdateStatus', {
          invoices: invoicesPayload,
        })

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.invoices-printed-successfully'),
          },
        })

        this.clearSelection()
        this.bulkPrintInvoicesData = []
        await this.fetchInvoices()
      } catch (error) {
        console.error('Error updating invoices status:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text:
              error.response?.data?.message || this.$t('common.error-occurred'),
          },
        })
      }
    },
    getBulkPatientData(invoice) {
      if (invoice.patient_card) {
        return {
          ...invoice.patient_card,
          patient: invoice.patient_card.patient
            ? { ...invoice.patient_card.patient }
            : null,
        }
      }
      return null
    },
    getBulkIncubatorName(invoice) {
      if (invoice.type === 'invoice_nicu') {
        const incubator = invoice.patient_card_incubator
        return incubator && incubator.incubator
          ? incubator.incubator.name
          : 'N/A'
      }
      return null
    },
    getBulkPatientDataForTest(invoice) {
      // Similar to getPatientDataForTestInvoice but for bulk
      if (invoice.patient_card && invoice.patient_card.patient) {
        return {
          ...invoice.patient_card.patient,
        }
      }
      return null
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style scoped>
/* Card View Styles */
.invoice-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.invoice-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.08);
  border-color: #d1d5db;
}

/* Card Header */
.card-header-custom {
  padding: 0.625rem 0.875rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 1px solid #e9ecef;
}

.invoice-number {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.2;
  margin: 0;
}

/* Card Body */
.card-body-custom {
  padding: 0.75rem 0.875rem;
}

.invoice-amount-section {
  text-align: center;
  padding: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  color: white;
}

.amount-label {
  font-size: 0.875rem;
  opacity: 0.9;
  margin-bottom: 0.25rem;
}

.amount-value {
  font-size: 1.5rem;
  font-weight: 700;
}

.info-section {
  margin-bottom: 0.5rem;
}

.info-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.info-item:last-child {
  border-bottom: none;
}

.info-icon {
  color: #4b5563;
  flex-shrink: 0;
}

.info-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  margin-right: 0.5rem;
  min-width: 60px;
}

.info-value {
  font-size: 0.9375rem;
  color: #374151;
  font-weight: 500;
  flex: 1;
  text-align: right;
}

.description-text {
  font-size: 0.875rem;
  color: #6b7280;
  font-style: italic;
  padding: 0.5rem;
  background: #f9fafb;
  border-radius: 4px;
}

/* Card Footer */
.card-footer-custom {
  padding: 0.625rem 0.875rem;
  background-color: #f9fafb;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  justify-content: flex-end;
}

.action-btn {
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 0.3125rem 0.625rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  border-width: 1.5px;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.action-btn .feather-icon {
  vertical-align: middle;
}

.hidden {
  display: none !important;
}

/* Bulk Print Styles */
#BulkPrintInvoices {
  display: none;
}

@media print {
  #BulkPrintInvoices {
    display: block;
  }

  #BulkPrintInvoices > * {
    page-break-after: always;
  }

  #BulkPrintInvoices > *:last-child {
    page-break-after: auto;
  }
}

.refund-summary-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  background-color: #f9fafb;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-top: 0.25rem;
  font-size: 0.9rem;
}

.summary-row.remaining {
  color: #0f5132;
}

.refund-history {
  border-top: 1px solid #e5e7eb;
  padding-top: 0.5rem;
}

.refund-history-item {
  padding: 0.35rem 0;
  border-bottom: 1px solid #f1f5f9;
}

.refund-history-item:last-child {
  border-bottom: none;
}

/* Status Tabs Styles */
.status-tabs-wrapper {
  margin-bottom: 1rem;
}

.status-tabs-custom {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  border: none;
  box-shadow: none;
}

.status-tabs-custom .status-tab-btn {
  min-width: 120px;
  padding: 0.625rem 1.25rem;
  font-size: 0.9375rem;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
}

.status-tabs-custom .status-tab-btn.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transform: translateY(-2px);
}

.status-tabs-custom .status-tab-btn.btn-outline-primary {
  background: #ffffff;
  border-color: #dee2e6;
  color: #495057;
}

.status-tabs-custom .status-tab-btn.btn-outline-primary:hover {
  background: #f8f9fa;
  border-color: #667eea;
  color: #667eea;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.status-tabs-custom .status-tab-btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.status-tabs-custom .status-tab-btn:active {
  transform: translateY(0);
}

/* Per Page Selector */
.per-page-selector {
  margin-right: 1rem;
}

.per-page-selector ::v-deep .form-group {
  margin-bottom: 0;
}

/* Bulk Selection Header */
.bulk-selection-header {
  background: linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 100%);
  border: 2px solid #667eea;
  border-radius: 8px;
  padding: 1rem;
}

.bulk-selection-header .gap-2 {
  gap: 0.5rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .invoice-card {
    margin-bottom: 1rem;
  }

  .status-tabs-custom {
    justify-content: center;
  }

  .status-tabs-custom .status-tab-btn {
    min-width: 100px;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    flex: 1 1 auto;
  }
}
</style>
